import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import WorkflowStates = Boo.Objects.Enums.WorkflowStates;
import { StrategyUpdateRequest } from '../shared/models/strategy-update/strategy-update';

@Injectable({
  providedIn: 'root'
})
export class CustomerCampaignService {
  private baseUrl = `${environment.urlConfig.serviceUrl}/customercampaign`;

  constructor(private http: HttpClient) { }

  localCampaignCsvFormat = ['Keyword', 'Location'];
  localCampaignExampleCsv = 'https://s3.amazonaws.com/s3staticfiles/keywordresearch/importexamples/LocalTrackingKeywordsExample.csv';
  nationalCampaignCsvFormat = ['Keyword'];
  nationalCampaignExampleCsv = 'https://s3.amazonaws.com/s3staticfiles/keywordresearch/importexamples/NationalTrackingKeywordsExample.csv';
  localCampaignInitialRankCsvFormat = ['Keyword', 'Location', 'Initial Rank', 'Initial Rank Date'];
  localCampaignInitialRankExampleCsv = 'https://s3.amazonaws.com/s3staticfiles/keywordresearch/importexamples/LocalTrackingKeywordsInitialRankExample.csv';
  nationalCampaignInitialRankCsvFormat = ['Keyword', 'Initial Rank', 'Initial Rank Date'];
  nationalCampaignInitialRankExampleCsv = 'https://s3.amazonaws.com/s3staticfiles/keywordresearch/importexamples/NationalTrackingKeywordsInitialRankExample.csv';

  getSummary(customerId: number): Observable<Boo.Objects.CustomerCampaign.CustomerCampaignSummary> {
    const params = new HttpParams().set('customerId', customerId);

    return this.http.get<Boo.Objects.CustomerCampaign.CustomerCampaignSummary>(`${this.baseUrl}/summary`, { params: params })
  }

  getTaskInfo(customerId: number, taskId: number): Observable<Boo.Objects.WebsiteUrlTask> {
    const params = new HttpParams()
      .set('customerId', customerId)
      .set('taskId', taskId);

    return this.http.get<Boo.Objects.WebsiteUrlTask>(`${this.baseUrl}/taskInfo`, { params: params });
  }

  save(customerId: number, websiteUrls: Boo.Objects.WebsiteUrl[], trackingKeywords: Boo.Objects.WebsiteKeywordTracking[], isCampaignNational?: boolean): Observable<any> {
    return this.http.post(`${this.baseUrl}/save`, {
      customerId: customerId,
      websiteUrls: websiteUrls,
      trackingKeywords: trackingKeywords,
      isCampaignNational: isCampaignNational
    });
  }

  getActiveTrackingKeywords(customerId: number): Observable<Boo.Objects.WebsiteKeywordTracking[]> {
    const params = new HttpParams().set('customerId', customerId);

    return this.http.get<Boo.Objects.WebsiteKeywordTracking[]>(`${this.baseUrl}/activeTrackingKeywords`, { params: params });
  }

  getWebsiteUrl(websiteUrlId: number): Observable<Boo.Objects.WebsiteUrl> {
    const params = new HttpParams().set('websiteUrlId', websiteUrlId);

    return this.http.get<Boo.Objects.WebsiteUrl>(`${this.baseUrl}/websiteUrl`, { params: params });
  }

  getWebsiteUrls(customerId: number, excludeInactiveKeywords?: boolean, websiteUrlStatuses?: Boo.Objects.Enums.WebsiteUrlStatuses[]): Observable<Boo.Objects.WebsiteUrl[]> {
    return this.http.post<Boo.Objects.WebsiteUrl[]>(`${this.baseUrl}/websiteUrls`, {
      customerId: customerId,
      excludeInactiveKeywords: !!excludeInactiveKeywords,
      websiteUrlStatuses: websiteUrlStatuses
    });
  }

  getKeywordHistory(websiteUrlId: number, since: Date): Observable<Boo.Objects.WebsiteUrlKeywordHistory[]> {
    const params = new HttpParams()
      .set('websiteUrlId', websiteUrlId)
      .set('since', since.toISOString());

    return this.http.get<Boo.Objects.WebsiteUrlKeywordHistory[]>(`${this.baseUrl}/keywordHistory`, { params: params });
  }

  requestStrategyUpdate(strategyUpdateRequest: StrategyUpdateRequest) : Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/requestStrategyUpdate`, strategyUpdateRequest);
  }

  getTaskTypeLabelMap(): Record<number, string> {
    return {
      [Boo.Objects.Enums.TaskTypeEnum.OnsiteRecommendation]: 'Onsite Recommendation',
      [Boo.Objects.Enums.TaskTypeEnum.HistoricOnsiteRecommendation]: 'Onsite Recommendation',
      [Boo.Objects.Enums.TaskTypeEnum.OnsiteRecommendationCreateContent]: 'Onsite Recommendation',
      [Boo.Objects.Enums.TaskTypeEnum.OnsiteCopyCreateContentStandard]: 'Onsite Copy',
      [Boo.Objects.Enums.TaskTypeEnum.OnsiteCopyCreateContentStandardRetired]: 'Onsite Copy',
      [Boo.Objects.Enums.TaskTypeEnum.PageCreationCreateContent]: 'Page Creation',
      [Boo.Objects.Enums.TaskTypeEnum.PageCreationRewriteContent]: 'Page Creation',
      [Boo.Objects.Enums.TaskTypeEnum.PageOptimizationCreateContent]: 'Page Optimization',
      [Boo.Objects.Enums.TaskTypeEnum.PageOptimizationRewriteContent]: 'Page Optimization'
    }
  }

  calculateReadOnlyState(websiteUrl: Boo.Objects.WebsiteUrl): void {
    websiteUrl.IsReadOnly = false;
    websiteUrl.ReadOnlyReason = '';

    if (websiteUrl.WebsiteUrlTask) {
      const terminalStates: number[] = [WorkflowStates.Completed, WorkflowStates.Canceled, WorkflowStates.Deleted, WorkflowStates.ReconcileCompleted];
      const uiBlockingTaskTypes: Boo.Objects.Enums.TaskTypeEnum[] = [
        Boo.Objects.Enums.TaskTypeEnum.OnsiteRecommendationCreateContent,
        Boo.Objects.Enums.TaskTypeEnum.OnsiteCopyCreateContentStandard,
        Boo.Objects.Enums.TaskTypeEnum.OnsiteCopyCreateContentStandardRetired,
        Boo.Objects.Enums.TaskTypeEnum.PageCreationCreateContent,
        Boo.Objects.Enums.TaskTypeEnum.PageCreationRewriteContent,
        Boo.Objects.Enums.TaskTypeEnum.PageOptimizationCreateContent,
        Boo.Objects.Enums.TaskTypeEnum.PageOptimizationRewriteContent
      ];

      const isInTerminalState = terminalStates.includes(websiteUrl.WebsiteUrlTask.WorkflowStateId);
      const isPendingWithoutRejectedPriorTasks = !websiteUrl.WebsiteUrlTask.HavePriorTasksBeenRejected && websiteUrl.WebsiteUrlTask.TaskStatusId === Boo.Objects.Enums.TaskStatusEnum.Pending;
      const isWorkblocked = websiteUrl.WebsiteUrlTask.IsWorkblocked;
      const isUIBlockingTaskType = uiBlockingTaskTypes.includes(websiteUrl.WebsiteUrlTask.TaskTypeId);

      if (!isInTerminalState && isUIBlockingTaskType) {
        websiteUrl.IsRestrictedFromStrategyUpdate = true;
        websiteUrl.RestrictionReason = `${this.getTaskTypeLabelMap()[websiteUrl.WebsiteUrlTask.TaskTypeId]} in progress`;

        if (!(isPendingWithoutRejectedPriorTasks || isWorkblocked)) {
          websiteUrl.IsReadOnly = true;
          websiteUrl.ReadOnlyReason = `${this.getTaskTypeLabelMap()[websiteUrl.WebsiteUrlTask.TaskTypeId]} in progress`;
        }
      }
    }

    const hasFutureWorkRequests = !!websiteUrl.WebsiteUrlFutureWorkRequests?.length;
    if (hasFutureWorkRequests) {
      websiteUrl.IsReadOnly = true;
      const futureWorkRequestsMessage = 'Future work scheduled for this url';
      websiteUrl.ReadOnlyReason = websiteUrl.ReadOnlyReason ? `${websiteUrl.ReadOnlyReason} | ${futureWorkRequestsMessage}` : futureWorkRequestsMessage;
    }
  }

  normalizeKeyword(keyword: string): string {
    return keyword
      .replaceAll('‘', '\'')
      .replaceAll('’', '\'')
      .replaceAll('“', '"')
      .replaceAll('”', '"')
      .replace(/\s+/g, ' ')
      .trim();
  }

  normalizeArea(area: string): string {
    return area.replace(/\s+/g, ' ').trim();
  }

  getKeywordPhrase(keyword: string, area: string, isAreaFirst: boolean): string {
    return (isAreaFirst ? this.normalizeArea(area) + ' ' + this.normalizeKeyword(keyword) : this.normalizeKeyword(keyword) + ' ' + this.normalizeArea(area)).trim();
  }

  activeStatuses(): Boo.Objects.Enums.WebsiteUrlStatuses[] {
    return [Boo.Objects.Enums.WebsiteUrlStatuses.Target, Boo.Objects.Enums.WebsiteUrlStatuses.Supporting, Boo.Objects.Enums.WebsiteUrlStatuses.New];
  }

  isActiveStatus(websiteUrl: Boo.Objects.WebsiteUrl): boolean {
    return this.activeStatuses().includes(websiteUrl.StatusId);
  }

  domainMatches(customerUrl: string, pageUrl: string): boolean {
    const url: uri.URI = new URI(pageUrl.toLowerCase());
    const matchUrl: uri.URI = new URI(customerUrl.toLowerCase());
    return url.normalize().hostname() === matchUrl.normalize().hostname() && url.normalize().protocol() === matchUrl.normalize().protocol();
  }

  updateWebsiteUrlStatus(
    websiteUrls: Boo.Objects.WebsiteUrl[],
    trackingKeywords: Boo.Objects.WebsiteKeywordTracking[],
    archivedWebsiteUrls: Boo.Objects.ArchivedWebsiteUrl[],
    archivedKeywords: Boo.Objects.ArchivedKeyword[],
    firstPageKeywordPhrases: string[],
    websiteUrl: Boo.Objects.WebsiteUrl,
    status: Boo.Objects.Enums.WebsiteUrlStatuses): void {
    let firstPageKeywordsMovedToTracking = false;
    websiteUrl.StatusId = status;
    websiteUrl.IsModified = true;

    if (status == Boo.Objects.Enums.WebsiteUrlStatuses.Archived) {
      websiteUrl.Keywords
        .filter(keyword => keyword.IsActive)
        .forEach(keyword => {
          if (firstPageKeywordPhrases.includes(keyword.KeywordPhrase.trim())) {
            firstPageKeywordsMovedToTracking = true;
            this.moveKeywordToTracking(websiteUrl.CustomerId, trackingKeywords, keyword, firstPageKeywordPhrases);
          } else {
            this.archiveKeyword(archivedKeywords, keyword, websiteUrl)
          }
        });

      archivedWebsiteUrls.push({
        WebsiteUrlId: websiteUrl.WebsiteUrlId,
        Url: websiteUrl.Url,
        InsertedDate: websiteUrl.InsertedDate,
        LatestArchiveDate: new Date()
      } as Boo.Objects.ArchivedWebsiteUrl);

      if (!websiteUrl.WebsiteUrlId) {
        websiteUrls.splice(websiteUrls.indexOf(websiteUrl), 1);
      }
    }

    if (status === Boo.Objects.Enums.WebsiteUrlStatuses.Supporting || status === Boo.Objects.Enums.WebsiteUrlStatuses.New) {
      websiteUrl.Keywords.forEach(keyword => {
        if (keyword.IsActive && firstPageKeywordPhrases.includes(keyword.KeywordPhrase.trim())) {
          firstPageKeywordsMovedToTracking = true;
          this.moveKeywordToTracking(websiteUrl.CustomerId, trackingKeywords, keyword, firstPageKeywordPhrases);
        }
      });
    }
    
    if (firstPageKeywordsMovedToTracking) {
     toastr.info('First page keywords were moved to Tracking Keywords.');
    }
  }

  updateArchivedWebsiteUrlStatus(
    customerId: number,
    websiteUrls: Boo.Objects.WebsiteUrl[],
    archivedWebsiteUrls: Boo.Objects.ArchivedWebsiteUrl[],
    archivedWebsiteUrl: Boo.Objects.ArchivedWebsiteUrl,
    status: Boo.Objects.Enums.WebsiteUrlStatuses): void {
    const websiteUrl = websiteUrls.find(x => x.WebsiteUrlId === archivedWebsiteUrl.WebsiteUrlId);
    if (websiteUrl) {
      websiteUrl.StatusId = status;
      websiteUrl.Keywords.forEach(keyword => { keyword.IsActive = false; });
      websiteUrl.IsModified = true;
    } else {
      websiteUrls.push({
        CustomerId: customerId,
        Description: '',
        IsHomePage: false,
        KeywordPhrases: '',
        Keywords: [],
        WebsiteUrlTask: null,
        StatusId: status,
        Url: archivedWebsiteUrl.Url,
        WebsiteUrlId: archivedWebsiteUrl.WebsiteUrlId,
        IsModified: true
      } as Boo.Objects.WebsiteUrl);
    }

    archivedWebsiteUrls.splice(archivedWebsiteUrls.indexOf(archivedWebsiteUrl), 1);
  }

  archiveKeyword(
    archivedKeywords: Boo.Objects.ArchivedKeyword[],
    websiteUrlKeyword: Boo.Objects.WebsiteUrlKeyword,
    websiteUrl: Boo.Objects.WebsiteUrl): void {
    archivedKeywords.push({
      KeywordId: websiteUrlKeyword.KeywordId,
      Keyword: websiteUrlKeyword.Keyword,
      AreaId: websiteUrlKeyword.AreaId,
      Area: websiteUrlKeyword.Area,
      IsAreaFirst: websiteUrlKeyword.IsAreaFirst,
      KeywordPhrase: websiteUrlKeyword.KeywordPhrase,
      ProRankTrackerUrlTermId: websiteUrlKeyword.ProRankTrackerUrlTermId,
      InitialRank: websiteUrlKeyword.InitialRank,
      InitialRankDate: websiteUrlKeyword.InitialRankDate,
      InsertedDate: websiteUrlKeyword.InsertedDate,
      WebsiteUrlId: websiteUrlKeyword.WebsiteUrlId,
      WebsiteUrl: websiteUrl.Url,
      WebsiteUrlKeywordId: websiteUrlKeyword.WebsiteUrlKeywordId,
      WebsiteKeywordTrackingId: null,
      LatestArchiveDate: new Date()
    } as Boo.Objects.ArchivedKeyword);

    if (websiteUrlKeyword.WebsiteUrlKeywordId) {
      websiteUrlKeyword.IsActive = false;
      websiteUrlKeyword.ProRankTrackerUrlTermId = null;
      websiteUrlKeyword.IsModified = true;
    } else {
      websiteUrl.Keywords.splice(websiteUrl.Keywords.indexOf(websiteUrlKeyword), 1);
    }
  }

  moveKeywordToWebsiteUrl(
    websiteUrlKeyword: Boo.Objects.WebsiteUrlKeyword,
    destinationWebsiteUrl: Boo.Objects.WebsiteUrl): void {
    const existingKeyword = destinationWebsiteUrl.Keywords.find(x => x.KeywordPhrase.toLowerCase().trim() === websiteUrlKeyword.KeywordPhrase.toLowerCase().trim());

    if (existingKeyword) {
      existingKeyword.IsActive = true;
      existingKeyword.IsRecommended = false;
      existingKeyword.ProRankTrackerUrlTermId = websiteUrlKeyword.ProRankTrackerUrlTermId;
      existingKeyword.InitialRank = existingKeyword.InitialRank || websiteUrlKeyword.InitialRank;
      existingKeyword.InitialRankDate = existingKeyword.InitialRankDate || websiteUrlKeyword.InitialRankDate;
      existingKeyword.Rank = existingKeyword.Rank || websiteUrlKeyword.Rank;
      existingKeyword.IsModified = true;
    } else {
      destinationWebsiteUrl.Keywords.push({
        AreaId: websiteUrlKeyword.AreaId,
        Area: websiteUrlKeyword.Area,
        IsActive: true,
        IsAreaFirst: websiteUrlKeyword.IsAreaFirst,
        IsRecommended: false,
        KeywordId: websiteUrlKeyword.KeywordId,
        Keyword: websiteUrlKeyword.Keyword,
        KeywordPhrase: websiteUrlKeyword.KeywordPhrase,
        ProRankTrackerUrlTermId: websiteUrlKeyword.ProRankTrackerUrlTermId,
        InitialRank: websiteUrlKeyword.InitialRank,
        InitialRankDate: websiteUrlKeyword.InitialRankDate,
        WebsiteUrlId: destinationWebsiteUrl.WebsiteUrlId,
        Rank: websiteUrlKeyword.Rank,
        IsModified: true
      } as Boo.Objects.WebsiteUrlKeyword);
    }

    websiteUrlKeyword.IsActive = false;
    websiteUrlKeyword.IsModified = true;
    websiteUrlKeyword.ProRankTrackerUrlTermId = null;
  }

  moveKeywordToTracking(
    customerId: number,
    trackingKeywords: Boo.Objects.WebsiteKeywordTracking[],
    websiteUrlKeyword: Boo.Objects.WebsiteUrlKeyword,
    firstPageKeywordPhrases: string[]): void {
    const existingTrackingKeyword = trackingKeywords.find(x => x.KeywordPhrase.toLowerCase().trim() === websiteUrlKeyword.KeywordPhrase.toLowerCase().trim());

    const wasFirstPage = firstPageKeywordPhrases.includes(websiteUrlKeyword.KeywordPhrase.trim());

    if (existingTrackingKeyword) {
      existingTrackingKeyword.IsActive = true;
      existingTrackingKeyword.IsModified = true;
      existingTrackingKeyword.ProRankTrackerUrlTermId = websiteUrlKeyword.ProRankTrackerUrlTermId;
      existingTrackingKeyword.InitialRank = existingTrackingKeyword.InitialRank || websiteUrlKeyword.InitialRank;
      existingTrackingKeyword.InitialRankDate = existingTrackingKeyword.InitialRankDate || websiteUrlKeyword.InitialRankDate;
      existingTrackingKeyword.Rank = existingTrackingKeyword.Rank || websiteUrlKeyword.Rank;
      existingTrackingKeyword.WasFirstPage = existingTrackingKeyword.WasFirstPage || wasFirstPage;
    } else {
      trackingKeywords.push({
        AreaId: websiteUrlKeyword.AreaId,
        Area: websiteUrlKeyword.Area,
        CustomerId: customerId,
        IsActive: true,
        IsAreaFirst: websiteUrlKeyword.IsAreaFirst,
        KeywordId: websiteUrlKeyword.KeywordId,
        Keyword: websiteUrlKeyword.Keyword,
        KeywordPhrase: websiteUrlKeyword.KeywordPhrase,
        ProRankTrackerUrlTermId: websiteUrlKeyword.ProRankTrackerUrlTermId,
        InitialRank: websiteUrlKeyword.InitialRank,
        InitialRankDate: websiteUrlKeyword.InitialRankDate,
        WasFirstPage: wasFirstPage,
        Rank: websiteUrlKeyword.Rank,
        IsModified: true
      } as Boo.Objects.WebsiteKeywordTracking);
    }

    websiteUrlKeyword.IsActive = false;
    websiteUrlKeyword.IsModified = true;
    websiteUrlKeyword.ProRankTrackerUrlTermId = null;
  }

  archiveTrackingKeyword(
    archivedKeywords: Boo.Objects.ArchivedKeyword[],
    trackingKeywords: Boo.Objects.WebsiteKeywordTracking[],
    trackingKeyword: Boo.Objects.WebsiteKeywordTracking): void {
    archivedKeywords.push({
      KeywordId: trackingKeyword.KeywordId,
      Keyword: trackingKeyword.Keyword,
      AreaId: trackingKeyword.AreaId,
      Area: trackingKeyword.Area,
      IsAreaFirst: trackingKeyword.IsAreaFirst,
      KeywordPhrase: trackingKeyword.KeywordPhrase,
      ProRankTrackerUrlTermId: trackingKeyword.ProRankTrackerUrlTermId,
      InitialRank: trackingKeyword.InitialRank,
      InitialRankDate: trackingKeyword.InitialRankDate,
      InsertedDate: trackingKeyword.InsertedDate,
      WebsiteUrlId: null,
      WebsiteUrl: null,
      WebsiteUrlKeywordId: null,
      WebsiteKeywordTrackingId: trackingKeyword.WebsiteKeywordTrackingId,
      LatestArchiveDate: new Date()
    } as Boo.Objects.ArchivedKeyword);

    if (trackingKeyword.WebsiteKeywordTrackingId) {
      trackingKeyword.IsActive = false;
      trackingKeyword.ProRankTrackerUrlTermId = null;
      trackingKeyword.IsModified = true;
    } else {
      trackingKeywords.splice(trackingKeywords.indexOf(trackingKeyword), 1);
    }
  }

  moveTrackingKeywordToWebsiteUrl(
    trackingKeyword: Boo.Objects.WebsiteKeywordTracking,
    websiteUrl: Boo.Objects.WebsiteUrl): void {
    const existingKeyword = websiteUrl.Keywords.find(x => x.KeywordPhrase.toLowerCase().trim() === trackingKeyword.KeywordPhrase.toLowerCase().trim());

    if (existingKeyword) {
      existingKeyword.IsActive = true;
      existingKeyword.IsRecommended = false;
      existingKeyword.ProRankTrackerUrlTermId = trackingKeyword.ProRankTrackerUrlTermId;
      existingKeyword.InitialRank =  trackingKeyword.InitialRank || existingKeyword.InitialRank;
      existingKeyword.InitialRankDate = trackingKeyword.InitialRankDate || existingKeyword.InitialRankDate;
      existingKeyword.Rank = existingKeyword.Rank || trackingKeyword.Rank;
      existingKeyword.IsModified = true
    } else {
      websiteUrl.Keywords.push({
        AreaId: trackingKeyword.AreaId,
        Area: trackingKeyword.Area,
        IsActive: true,
        IsAreaFirst: trackingKeyword.IsAreaFirst,
        IsRecommended: false,
        KeywordId: trackingKeyword.KeywordId,
        Keyword: trackingKeyword.Keyword,
        KeywordPhrase: trackingKeyword.KeywordPhrase,
        ProRankTrackerUrlTermId: trackingKeyword.ProRankTrackerUrlTermId,
        InitialRank: trackingKeyword.InitialRank,
        InitialRankDate: trackingKeyword.InitialRankDate,
        WebsiteUrlId: websiteUrl.WebsiteUrlId,
        Rank: trackingKeyword.Rank,
        IsModified: true
      } as Boo.Objects.WebsiteUrlKeyword);
    }

    trackingKeyword.IsActive = false;
    trackingKeyword.IsModified = true;
    trackingKeyword.ProRankTrackerUrlTermId = null;
  }

  moveArchivedKeywordToTracking(
    customerId: number,
    trackingKeywords: Boo.Objects.WebsiteKeywordTracking[],
    archivedKeywords: Boo.Objects.ArchivedKeyword[],
    archivedKeyword: Boo.Objects.ArchivedKeyword,
    firstPageKeywordPhrases: string[]): void {
    const existingTrackingKeyword = trackingKeywords.find(x => x.KeywordPhrase.toLowerCase().trim() === archivedKeyword.KeywordPhrase.toLowerCase().trim());

    const wasFirstPage = firstPageKeywordPhrases.includes(archivedKeyword.KeywordPhrase.trim());

    if (existingTrackingKeyword) {
      existingTrackingKeyword.IsActive = true;
      existingTrackingKeyword.IsModified = true;
      existingTrackingKeyword.ProRankTrackerUrlTermId = archivedKeyword.ProRankTrackerUrlTermId;
      existingTrackingKeyword.InitialRank = existingTrackingKeyword.InitialRank || archivedKeyword.InitialRank;
      existingTrackingKeyword.InitialRankDate = existingTrackingKeyword.InitialRankDate || archivedKeyword.InitialRankDate;
      existingTrackingKeyword.WasFirstPage = existingTrackingKeyword.WasFirstPage || wasFirstPage;
    } else {
      trackingKeywords.push({
        AreaId: archivedKeyword.AreaId,
        Area: archivedKeyword.Area,
        CustomerId: customerId,
        IsActive: true,
        IsAreaFirst: archivedKeyword.IsAreaFirst,
        KeywordId: archivedKeyword.KeywordId,
        Keyword: archivedKeyword.Keyword,
        KeywordPhrase: archivedKeyword.KeywordPhrase,
        ProRankTrackerUrlTermId: archivedKeyword.ProRankTrackerUrlTermId,
        InitialRank: archivedKeyword.InitialRank,
        InitialRankDate: archivedKeyword.InitialRankDate,
        WasFirstPage: wasFirstPage,
        IsModified: true
      } as Boo.Objects.WebsiteKeywordTracking);
    }

    archivedKeywords.splice(archivedKeywords.indexOf(archivedKeyword), 1);
  }

  moveArchivedKeywordToWebsiteUrl(
    archivedKeywords: Boo.Objects.ArchivedKeyword[],
    archivedKeyword: Boo.Objects.ArchivedKeyword,
    websiteUrl: Boo.Objects.WebsiteUrl): void {
    const existingKeyword = websiteUrl.Keywords.find(x => x.KeywordPhrase.toLowerCase().trim() === archivedKeyword.KeywordPhrase.toLowerCase().trim());

    if (existingKeyword) {
      existingKeyword.IsActive = true;
      existingKeyword.IsRecommended = false;
      existingKeyword.ProRankTrackerUrlTermId = archivedKeyword.ProRankTrackerUrlTermId;
      existingKeyword.InitialRank = existingKeyword.InitialRank || archivedKeyword.InitialRank;
      existingKeyword.InitialRankDate = existingKeyword.InitialRankDate || archivedKeyword.InitialRankDate;
      existingKeyword.IsModified = true;
    } else {
      websiteUrl.Keywords.push({
        AreaId: archivedKeyword.AreaId,
        Area: archivedKeyword.Area,
        IsActive: true,
        IsAreaFirst: archivedKeyword.IsAreaFirst,
        IsRecommended: false,
        KeywordId: archivedKeyword.KeywordId,
        Keyword: archivedKeyword.Keyword,
        KeywordPhrase: archivedKeyword.KeywordPhrase,
        ProRankTrackerUrlTermId: archivedKeyword.ProRankTrackerUrlTermId,
        InitialRank: archivedKeyword.InitialRank,
        InitialRankDate: archivedKeyword.InitialRankDate,
        WebsiteUrlId: websiteUrl.WebsiteUrlId,
        IsModified: true
      } as Boo.Objects.WebsiteUrlKeyword);
    }

    archivedKeywords.splice(archivedKeywords.indexOf(archivedKeyword), 1);
  }

  toggleIsRecommended(websiteUrlKeyword: Boo.Objects.WebsiteUrlKeyword): void {
    websiteUrlKeyword.IsRecommended = !websiteUrlKeyword.IsRecommended;
    websiteUrlKeyword.IsModified = true;
  }
}
